<template>
    <div :class="['page', { 'page--embed': partnerAppEmbedCustomization }]">
        <div class="row gradient-background">
            <div class="wrapper wrapper--no-max-width">
                <header :class="['row__header', { 'row__header--partner-app': isPartnerApp }]">
                    <back-button
                        :isAbsolute="false"
                        :isBlack="true"
                        :isHiddenOnDesktop="true"
                    />
                    <h1>{{ $t('profile.aboutUs') }}</h1>
                </header>
                <links-section
                    :sectionHeaderText="$t('aboutUs.followUs')"
                    :sectionItems="sections.social"
                />
                <links-section
                    :sectionHeaderText="$t('aboutUs.legal')"
                    :sectionItems="legalLinks"
                />
                <links-section
                    :sectionHeaderText="$t('aboutUs.careers')"
                    :sectionItems="sections.careers"
                />
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.page {
    color: $cyprus-color;
}
</style>

<script>
// 3rd party libs
import {
    mapActions, mapGetters,
} from 'vuex';

// Components
import linksSection from '@webapp/components/links-section/links-section.vue';
import backButton from '@webapp/components/back-button/back-button.vue';

// Config
import {
    ServerConfigurationProps,
} from '@shared/types/config';

// Socios variables
import socios from '/socios.json';

// Utils
import {
    getUserAgreementLink,
} from '@shared/utils/user-agreement';

export default {
    name: 'about-us',
    components: {
        linksSection,
        backButton,
    },
    data() {
        return {
            termsAndConditions: null,
        };
    },
    computed: {
        ...mapGetters([
            'isPartnerApp',
            'localeWithUnderscore',
            'localeISO2',
            'isUsaRegulation',
            'partnerAppEmbedCustomization',
        ]),

        ...mapGetters('agreements', ['acceptedUserAgreement']),

        ...mapGetters('serverConfig', ['serverConfigByKey']),

        sections() {
            return {
                social: [
                    {
                        title: this.$t('aboutUs.social.facebook.name'),
                        command: this.$t('aboutUs.social.facebook.command'),
                        icon: 'facebook',
                        url: 'https://www.facebook.com/sociosdotcom/',
                        isExternal: true,
                    },
                    {
                        title: this.$t('aboutUs.social.twitter.name'),
                        command: this.$t('aboutUs.social.twitter.command'),
                        icon: 'twitter',
                        url: 'https://twitter.com/socios',
                        isExternal: true,
                    },
                    {
                        title: this.$t('aboutUs.social.instagram.name'),
                        command: this.$t('aboutUs.social.instagram.command'),
                        icon: 'instagram',
                        url: 'https://www.instagram.com/sociosdotcom/',
                        isExternal: true,
                    },
                    {
                        title: this.$t('aboutUs.social.linkedin.name'),
                        command: this.$t('aboutUs.social.linkedin.command'),
                        icon: 'linkedin',
                        url: 'https://www.linkedin.com/company/socios-com/',
                        isExternal: true,
                    },
                    {
                        title: this.$t('aboutUs.social.medium.name'),
                        command: this.$t('aboutUs.social.medium.command'),
                        icon: 'medium',
                        url: 'https://medium.com/socios',
                        isExternal: true,
                    },
                    {
                        title: this.$t('aboutUs.social.youtube.name'),
                        command: this.$t('aboutUs.social.youtube.command'),
                        icon: 'youtube',
                        url: 'https://www.youtube.com/channel/UCzglT2jQzKy7z9hNFXN8geg',
                        isExternal: true,
                    },
                    {
                        title: this.$t('aboutUs.social.weibo.name'),
                        command: this.$t('aboutUs.social.weibo.command'),
                        icon: 'weibo',
                        url: 'https://weibo.com/sociosdotcom?is_all=1',
                        isExternal: true,
                    },
                ],
                legal: [
                    {
                        title: this.$t('more.privacyPolicy'),
                        url: this.serverConfigByKey(ServerConfigurationProps.privacyPolicyLink),
                        isExternal: true,
                    },
                    {
                        title: this.$t('more.termsAndConditions'),
                        url: this.termsAndConditions,
                        isExternal: true,
                    },
                    {
                        title: this.$t('more.userGuidelines'),
                        url: 'https://www.socios.com/socios-com-user-guidelines/',
                        isExternal: true,
                    },
                    {
                        title: this.$t('more.fantokensCom'),
                        url: 'https://www.fantokens.com/',
                        isExternal: true,
                        hideForUsaRegulation: true,
                    },
                    {
                        title: this.$t('more.insiderTradingPolicy'),
                        url: 'https://www.socios.com/insider-trading-policy/',
                        isExternal: true,
                        hideForUsaRegulation: true,
                    },
                    {
                        title: this.$t('more.responsibleTrading'),
                        url: 'https://www.socios.com/responsible-operation-responsible-trading/',
                        isExternal: true,
                        hideForUsaRegulation: true,
                    },
                    {
                        title: this.$t('more.cookiesPolicy'),
                        url: 'https://www.socios.com/cookies-policy/',
                        isExternal: true,
                    },
                ],
                careers: [
                    {
                        title: this.$t('more.comeJoinUs'),
                        url: 'https://www.chiliz.com/en/careers/',
                        isExternal: true,
                    },
                ],
            };
        },

        legalLinks() {
            if (this.isUsaRegulation) {
                return this.sections.legal.filter((item) => !item.hideForUsaRegulation);
            }

            return this.sections.legal;
        },
    },
    watch: {
        acceptedUserAgreement: {
            immediate: true,
            handler(newValue) {
                this.termsAndConditions = newValue.link ? getUserAgreementLink(newValue) : `${socios.cmsUrl}/${this.localeWithUnderscore}/terms-and-conditions`;
            },
        },
    },
    mounted() {
        this.fetchAcceptedUserAgreement({
            details: ['agreementVersionTranslations'],
            locales: [this.localeISO2],
        });
    },
    methods: {
        ...mapActions('agreements', ['fetchAcceptedUserAgreement']),

    },
};
</script>